import Layout from '@/components/Layout'
import { GAME_LIST, getGameCategory } from '../../utils/gameList'
import { getGameReport } from '../../services/api'
import api from '../../services/api'
import { API_ENDPOINTS } from '../../config/api'
import { useState, useEffect, useMemo } from 'react'

/**
 * GameReport Component
 * 
 * Page for viewing game reports with filters and export options.
 */
function GameReport() {
  const [entriesPerPage, setEntriesPerPage] = useState(50)
  const [searchValue, setSearchValue] = useState('')
  const [eventName, setEventName] = useState('All')
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [toDate, setToDate] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [userSearch, setUserSearch] = useState('')
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [count, setCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [recommendations, setRecommendations] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)

  const fetchReport = async (page = 1) => {
    setLoading(true)
    try {
      const offset = (page - 1) * entriesPerPage

      const category = getGameCategory(eventName) || 'game' // Fallback or strict?

      const params = {
        category: category,
        event: eventName === 'All' ? undefined : eventName,
        fromDate,
        toDate,
        limit: entriesPerPage,
        offset: offset,
        search: userSearch || undefined
      }

      const response = await getGameReport(params)

      if (response && response.success) {
        setTableData(response.data || [])
        setCount(response.count || 0)
      } else {
        setTableData([])
        setCount(0)
      }
    } catch (error) {
      console.error('Error fetching game report:', error)
      setTableData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchReport(currentPage)
  }, [currentPage, entriesPerPage])

  // Debounced User Search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (userSearch.length >= 3) {
        fetchUserSuggestions(userSearch)
      } else {
        setRecommendations([])
        setShowDropdown(false)
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [userSearch])

  const fetchUserSuggestions = async (searchTerm) => {
    setSearchLoading(true)
    setShowDropdown(true)
    setRecommendations([])
    try {
      const response = await api.get(API_ENDPOINTS.SEARCH_BY_USERNAME, {
        params: { username: searchTerm }
      })
      if (response.data?.data?.results && Array.isArray(response.data.data.results)) {
        setRecommendations(response.data.data.results)
      } else {
        setRecommendations([])
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error)
      setRecommendations([])
    } finally {
      setSearchLoading(false)
    }
  }

  const handleSelectUser = (username) => {
    setUserSearch(username)
    setShowDropdown(false)
    setRecommendations([])
  }

  const handleLoad = () => {
    setCurrentPage(1)
    fetchReport(1)
  }

  // Pagination is now server-side driven mostly, but keeping client side slice if API returns all
  // The API seems to support limit/offset.
  // Ideally we should use total records from API for pages, but for now lets assume tableData contains the current page data

  const totalPages = Math.ceil(count / entriesPerPage)

  const paginatedData = tableData // The API returns the data for the current page

  // Handle Load button click


  // Handle Download CSV
  const handleDownloadCSV = () => {
    console.log('Downloading CSV...')
    // TODO: Implement CSV download
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        {/* Header Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-start mb-2">
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Game Report
            </div>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Filter Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-10px" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">Event Name</label>
              <select
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none min-w-[200px]"
              >
                <option value="All">All</option>
                {GAME_LIST.map((game, index) => (
                  <option key={`${game.value}-${index}`} value={game.value}>
                    {game.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-white text-[12px] font-bold mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-2 py-2 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              />
            </div>

            <div className="flex flex-col relative">
              <label className="text-white text-[12px] font-bold mb-1">Search by user</label>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value)
                  setShowDropdown(false)
                }}
                placeholder="Enter Atleast 3 character"
                className="px-2 py-2 text-[11px] bg-white text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              />
              {showDropdown && (
                <ul className="absolute top-full left-0 w-full bg-white text-black text-[12px] rounded-sm shadow-lg max-h-40 overflow-y-auto z-50">
                  {searchLoading ? (
                    <li className="px-2 py-2 text-gray-500 italic">Searching...</li>
                  ) : recommendations.length > 0 ? (
                    recommendations.map((user, index) => (
                      <li
                        key={index}
                        onClick={() => handleSelectUser(user.username)}
                        className="px-2 py-2 cursor-pointer hover:bg-blue-100"
                      >
                        {user.username}
                      </li>
                    ))
                  ) : (
                    <li className="px-2 py-2 text-gray-500 italic">No record found</li>
                  )}
                </ul>
              )}
            </div>

            <button
              onClick={handleLoad}
              disabled={loading}
              className="px-6 py-2 bg-[#333d79] text-white font-medium text-[12px] rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Load'}
            </button>

            <button
              onClick={handleDownloadCSV}
              className="px-6 py-2 bg-[#11374a] text-white font-medium text-[12px] rounded-sm hover:bg-[#35b840] transition-colors"
            >
              Download CSV
            </button>
          </div>

          {/* Table Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
            <div className="flex items-center gap-2">
              <span className="text-white text-[12px] font-bold">Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 text-[12px] bg-white text-black rounded-sm border-none focus:outline-none"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
                <option value="500">500</option>
              </select>
              <span className="text-white text-[12px] font-bold">entries</span>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-white text-[12px] font-bold">Search:</label>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Type to Search"
                className="px-2 py-1 text-[12px] bg-white text-black placeholder-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 py-2"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Date
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Description
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Credit
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Debit
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Closing
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-2 py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-2 py-8 text-center text-gray-400">
                      There are no records to show
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr
                      key={row.bet_id || index}
                      className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                        }`}
                    >
                      <td className="py-1 px-2 text-white text-[13px]">
                        {/* Format date if needed, or use settled_at */}
                        {row.settled_at ? new Date(row.settled_at).toLocaleString() : '-'}
                      </td>
                      <td className="py-1 px-2 text-[#2ce5dd] text-[13px]">
                        -
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.profit || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {/* Mapping debit to loss as per inference, handle null */}
                        {row.loss || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.closing || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-end items-center gap-2 mt-4">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                «
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 text-[14px] font-bold transition-colors ${currentPage === page
                    ? "bg-[#333d79ff] text-white"
                    : "text-white hover:bg-gray-700"
                    }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ›
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-white text-[14px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                »
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default GameReport
